<template>
  <v-menu
    v-model="open"
    offset-y
    :close-on-content-click="false"
    min-width="340"
    max-width="420"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        dense
        hide-details
        outlined
        readonly
        class="rm-select"
        :label="dense ? filter.name + ' Filter' : null"
        :value="displayText"
        :title="selectionTitle"
        append-icon="mdi-menu-down"
        :clearable="filter.selected.length > 0"
        v-bind="attrs"
        v-on="on"
        @click:clear="clearAll"
      >
        <template v-slot:prepend-inner>
          <v-icon small class="mt-1">mdi-file-tree</v-icon>
        </template>
      </v-text-field>
    </template>
    <v-card>
      <v-card-text class="pa-2">
        <v-text-field
          v-model="search"
          dense
          outlined
          hide-details
          clearable
          autofocus
          placeholder="Type to search"
          prepend-inner-icon="mdi-magnify"
          class="mb-1"
        ></v-text-field>
        <v-progress-linear
          v-if="filter.narrowLoading"
          indeterminate
          height="2"
          class="mb-1"
        ></v-progress-linear>
        <div class="treeContainer">
          <v-treeview
            ref="tree"
            dense
            selectable
            open-on-click
            selection-type="independent"
            item-key="node_id"
            item-text="name"
            item-children="children"
            :items="filter.narrowedItems || filter.items"
            :search="search"
            :filter="matchItem"
            :value="filter.selected"
            @input="selectionChanged"
          >
            <template v-slot:label="{ item }">
              <span :title="item.name" style="font-size: 13px">{{
                item.name
              }}</span>
            </template>
          </v-treeview>
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script>
import { defineComponent, ref, computed, watch } from "vue";

export default defineComponent({
  name: "HierarchyTreeFilter",
  props: {
    // { name, items (nested {name, node_id, children} — treePickerOptionsActive),
    //   nodeMap, selected: [node_id], selectedNodes: [{node_id, level, name}],
    //   narrowedItems, narrowLoading, onOpen }
    filter: Object,
    dense: Boolean,
  },
  setup(props, { emit }) {
    const open = ref(false);
    const search = ref("");
    const tree = ref(null);

    const displayText = computed(() => {
      if (!props.filter.selectedNodes.length) return "";
      if (props.filter.selectedNodes.length === 1)
        return props.filter.selectedNodes[0].name;
      return `(${props.filter.selectedNodes.length} items)`;
    });

    const selectionTitle = computed(() =>
      props.filter.selectedNodes.length
        ? props.filter.selectedNodes.map((n) => n.name).join(", ")
        : props.filter.name + " Filter"
    );

    watch(search, (val) => {
      // expand branches while searching so nested matches are visible
      if (tree.value) tree.value.updateAll(!!val);
    });

    watch(open, (val) => {
      if (!val) search.value = "";
      // scope-on-open: the owner narrows the tree by the other active
      // filters only when the picker is actually opened
      else if (props.filter.onOpen) props.filter.onOpen(props.filter);
    });

    const matchItem = (item, searchText, textKey) =>
      item[textKey].toLowerCase().indexOf(searchText.toLowerCase()) > -1;

    const selectionChanged = (nodeIds) => {
      // the filter object is owned by the parent view and mutated in place,
      // matching how FilterOptions binds f.filterValues
      // eslint-disable-next-line vue/no-mutating-props
      props.filter.selected = nodeIds;
      // eslint-disable-next-line vue/no-mutating-props
      props.filter.selectedNodes = nodeIds
        .map((id) => props.filter.nodeMap[id])
        .filter((n) => n);
      emit("change", props.filter);
    };

    const clearAll = () => selectionChanged([]);

    return {
      open,
      search,
      tree,
      displayText,
      selectionTitle,
      matchItem,
      selectionChanged,
      clearAll,
    };
  },
});
</script>

<style lang="scss" scoped>
.treeContainer {
  max-height: 320px;
  overflow-y: auto;
}
::v-deep div.v-treeview-node__label {
  font-size: 13px;
}
::v-deep .v-treeview-node__root {
  padding: 0 !important;
  min-height: 26px !important;
}
</style>
